/**
 * Client sign-off: the client signs on the tablet (finger or pen) at the end of a survey or at
 * handover. The signature is a transparent PNG kept as a project asset, with the name written
 * by the signer, the date and time, the revision shown and an optional note (reserves). The
 * handover pack prints the latest handover sign-off. A sign-off is not edited afterwards: it
 * can only be removed (the client signs again).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { latestRevision, newId, type Op, type Project, type Signoff } from '@elec/domain';
import type { Locale, Translate } from '@elec/i18n';
import type { AssetStore } from './assets.ts';
import { Dialog } from './ui.tsx';
import { localeTag } from '@elec/i18n';

const W = 600;
const H = 200;

/** A drawing area for a signature: strokes in ink, read back as PNG bytes. */
export function SignaturePad(props: {
  t: Translate;
  onChange(empty: boolean): void;
  /** Filled with a function returning the PNG (null when nothing is drawn). */
  pad: { current: (() => Promise<Uint8Array | null>) | null };
  /** Change it to wipe the pad. */
  clear: number;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const inked = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext('2d')!;
    ctx.clearRect(0, 0, W, H);
    inked.current = false;
    props.onChange(true);
  }, [props.clear]);
  useEffect(() => {
    props.pad.current = () =>
      new Promise((resolve) => {
        const c = ref.current;
        if (!c || !inked.current) return resolve(null);
        c.toBlob(async (b) => resolve(b ? new Uint8Array(await b.arrayBuffer()) : null), 'image/png');
      });
    return () => {
      props.pad.current = null;
    };
  });
  const at = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return { x: ((e.clientX - r.left) * W) / r.width, y: ((e.clientY - r.top) * H) / r.height };
  };
  const line = (from: { x: number; y: number }, to: { x: number; y: number }, pressure: number) => {
    const ctx = ref.current!.getContext('2d')!;
    ctx.strokeStyle = '#10213d';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2 + 2.5 * (pressure || 0.5);
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();
  };
  return (
    <canvas
      ref={ref}
      width={W}
      height={H}
      className="signature-pad"
      aria-label={props.t('signoff.padLabel')}
      data-testid="signature-pad"
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        drawing.current = true;
        last.current = at(e);
        line(last.current, { x: last.current.x + 0.1, y: last.current.y + 0.1 }, e.pressure);
        if (!inked.current) {
          inked.current = true;
          props.onChange(false);
        }
      }}
      onPointerMove={(e) => {
        if (!drawing.current || !last.current) return;
        const p = at(e);
        line(last.current, p, e.pressure);
        last.current = p;
      }}
      onPointerUp={() => {
        drawing.current = false;
        last.current = null;
      }}
      onPointerCancel={() => {
        drawing.current = false;
        last.current = null;
      }}
    />
  );
}

export const signoffsOf = (p: Project): Signoff[] =>
  Object.values(p.signoffs ?? {}).sort((a, b) => b.at.localeCompare(a.at));

/** The latest sign-off of a kind (the handover pack prints the latest handover one). */
export const latestSignoff = (p: Project, kind: Signoff['kind']): Signoff | undefined =>
  signoffsOf(p).find((s) => s.kind === kind);

/** Sign here, and the sign-offs already given. Shared by the dialog and the site survey. */
export function SignoffEditor(props: {
  project: Project;
  t: Translate;
  locale: Locale;
  assets: AssetStore;
  kind: Signoff['kind'];
  readOnly?: boolean;
  dispatch(label: string, ops: Op[]): void;
  notify(msg: string): void;
}) {
  const { project, t } = props;
  const [name, setName] = useState(project.meta.ownerName);
  const [note, setNote] = useState('');
  const [empty, setEmpty] = useState(true);
  const [clear, setClear] = useState(0);
  const pad = useRef<(() => Promise<Uint8Array | null>) | null>(null);
  const list = signoffsOf(project);
  const revision = latestRevision(project)?.index;
  const save = async () => {
    const bytes = await pad.current?.();
    if (!bytes || !name.trim()) return;
    const id = newId();
    const asset = `signature-${id}.png`;
    await props.assets.add(asset, bytes, 'image/png', false);
    const s: Signoff = {
      id,
      kind: props.kind,
      at: new Date().toISOString(),
      name: name.trim(),
      signature: asset,
      ...(revision ? { revision } : {}),
      note: note.trim(),
    };
    props.dispatch(t('signoff.txAdd', { name: s.name }), [{ op: 'add', coll: 'signoffs', value: s }]);
    setNote('');
    setClear((n) => n + 1);
    props.notify(t('signoff.saved'));
  };
  return (
    <div className="signoff">
      {!props.readOnly && (
        <div className="signoff-new">
          <p className="muted small">
            {t(props.kind === 'handover' ? 'signoff.handoverText' : 'signoff.surveyText', {
              title: project.meta.title || '—',
              revision: revision ?? '—',
            })}
          </p>
          <label className="sv-field">
            <span>{t('signoff.name')}</span>
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="signoff-name" />
          </label>
          <label className="sv-field">
            <span>{t('signoff.note')}</span>
            <textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="signoff-note"
            />
          </label>
          <SignaturePad t={t} pad={pad} clear={clear} onChange={setEmpty} />
          <div className="row">
            <button onClick={() => setClear((n) => n + 1)} disabled={empty}>
              {t('signoff.clear')}
            </button>
            <button
              className="primary"
              disabled={empty || !name.trim()}
              onClick={() => void save()}
              data-testid="signoff-save"
            >
              {t('signoff.save')}
            </button>
          </div>
        </div>
      )}
      {list.length > 0 && (
        <ul className="signoff-list" data-testid="signoff-list">
          {list.map((s) => (
            <SignedItem key={s.id} s={s} {...props} />
          ))}
        </ul>
      )}
    </div>
  );
}

function SignedItem(props: {
  s: Signoff;
  t: Translate;
  locale: Locale;
  assets: AssetStore;
  readOnly?: boolean;
  dispatch(label: string, ops: Op[]): void;
}) {
  const { s, t } = props;
  const bytes = props.assets.bytes.get(s.signature);
  const url = useMemo(
    () =>
      bytes
        ? URL.createObjectURL(new Blob([bytes.slice().buffer as ArrayBuffer], { type: 'image/png' }))
        : '',
    [bytes],
  );
  useEffect(() => () => (url ? URL.revokeObjectURL(url) : undefined), [url]);
  const when = new Date(s.at).toLocaleString(localeTag(props.locale), {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
  return (
    <li>
      {url ? <img src={url} alt={t('signoff.of', { name: s.name })} /> : <span className="muted">—</span>}
      <div>
        <strong>{s.name}</strong> · {t(s.kind === 'handover' ? 'signoff.kindHandover' : 'signoff.kindSurvey')}
        <div className="muted small">
          {when}
          {s.revision ? ` · ${t('signoff.revision', { r: s.revision })}` : ''}
        </div>
        {s.note && <div className="small">{s.note}</div>}
      </div>
      {!props.readOnly && (
        <button
          className="link small"
          onClick={() =>
            props.dispatch(t('signoff.txRemove'), [{ op: 'remove', coll: 'signoffs', id: s.id }])
          }
        >
          {t('signoff.remove')}
        </button>
      )}
    </li>
  );
}

export function SignoffDialog(props: {
  project: Project;
  t: Translate;
  locale: Locale;
  assets: AssetStore;
  readOnly: boolean;
  dispatch(label: string, ops: Op[]): void;
  notify(msg: string): void;
  /** Download the handover pack (with the latest sign-off). */
  handoverPack(): void;
  onClose(): void;
}) {
  const { t } = props;
  return (
    <Dialog
      title={t('signoff.title')}
      onClose={props.onClose}
      actions={
        <>
          <button onClick={props.handoverPack} data-testid="signoff-pack">
            {t('signoff.pack')}
          </button>
          <button className="primary" onClick={props.onClose}>
            {t('dialog.close')}
          </button>
        </>
      }
    >
      <SignoffEditor {...props} kind="handover" />
    </Dialog>
  );
}
